import '../polyfills/object-has-own';
import { createRoot, hydrateRoot } from 'react-dom/client';
import type { Root } from 'react-dom/client';
import { FlareErrorBoundary } from '@flareapp/react';
import { router } from '@inertiajs/react';
import { initializeAuthenticatedAppFlare } from '../flare';
import { createDashboardInertiaApp } from './inertia';
import { initializeAccountAnalytics, trackAccountPage } from './analytics';
import '../../css/dashboard.css';

type InertiaRootElement = HTMLElement & { reactRoot?: Root };
const isFlareEnabled =
    !window.location.pathname.startsWith('/podcast-boost') && initializeAuthenticatedAppFlare();

function AppWithBoundaries({
    App,
    props,
}: {
    App: React.ComponentType<Record<string, unknown>>;
    props: Record<string, unknown>;
}) {
    const inertiaApp = <App {...props} />;

    return isFlareEnabled ? <FlareErrorBoundary>{inertiaApp}</FlareErrorBoundary> : inertiaApp;
}

createDashboardInertiaApp({
    setup({ el, App, props }) {
        initializeAccountAnalytics(props.initialPage);
        router.on('navigate', (event) => trackAccountPage(event.detail.page));
        const rootElement = el as InertiaRootElement;
        const app = (
            <AppWithBoundaries
                App={App as React.ComponentType<Record<string, unknown>>}
                props={props}
            />
        );

        if (rootElement.reactRoot) {
            rootElement.reactRoot.render(app);
            return;
        }

        if (rootElement.hasChildNodes()) {
            rootElement.reactRoot = hydrateRoot(rootElement, app);
            return;
        }

        const root = createRoot(rootElement);
        rootElement.reactRoot = root;
        root.render(app);
    },
});
